import React from 'react';
import { Megaphone, MousePointerClick, Pin, Sparkles, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useHighlightOverrideQuery,
  useHighlightPinsQuery,
  useSlotCardQuery,
} from '@/client/api/highlights/highlights.query';
import { PILLARS, SlotDef, describeDefaultSource, pillarName } from '@/client/types/highlights/slotRegistry';

/** Static accent classes per pillar colour (Tailwind can't build these at runtime). */
const ACCENT: Record<string, { ring: string; chip: string; glow: string }> = {
  emerald: { ring: 'hover:border-emerald-400', chip: 'bg-emerald-50 text-emerald-700', glow: 'group-hover:bg-emerald-50' },
  blue: { ring: 'hover:border-blue-400', chip: 'bg-blue-50 text-blue-700', glow: 'group-hover:bg-blue-50' },
  violet: { ring: 'hover:border-violet-400', chip: 'bg-violet-50 text-violet-700', glow: 'group-hover:bg-violet-50' },
  amber: { ring: 'hover:border-amber-400', chip: 'bg-amber-50 text-amber-700', glow: 'group-hover:bg-amber-50' },
  slate: { ring: 'hover:border-slate-300', chip: 'bg-slate-100 text-slate-600', glow: 'group-hover:bg-slate-50' },
};

const isEditable = (slot: SlotDef) => slot.feed === 'highlights' && slot.etid != null && !slot.dynamic;

interface Props {
  slot: SlotDef;
  onOpen: (slot: SlotDef) => void;
  /** Grid-placement classes for the mosaic/menu cell. */
  className?: string;
}

/**
 * One promoted position, drawn where it lives on the page. Shows in plain
 * language what fills it and whether an admin has customised it, and opens the
 * management sheet on click.
 */
export function SlotTile({ slot, onOpen, className }: Props) {
  const editable = isEditable(slot);
  const accent = ACCENT[(slot.etid != null && PILLARS[slot.etid]?.color) || 'slate'];

  // Only editable (highlights) slots have a single etid to look up; the hooks
  // self-disable for null/0, so hero/menu tiles fire no request.
  // Badges read the card the slot actually shows, not the pillar it sits under.
  const { data: slotCard } = useSlotCardQuery(editable ? slot : null);
  const cardEtid = slotCard?.etid ?? null;
  const { data: pins } = useHighlightPinsQuery(cardEtid);
  const { data: override } = useHighlightOverrideQuery(cardEtid);

  const pinCount = pins?.length ?? 0;
  const hasOverride = !!override;

  const status: { icon: React.ReactNode; label: string; cls: string } = (() => {
    if (slot.feed === 'promotions')
      return { icon: <Megaphone className="h-3 w-3" />, label: 'Promotions', cls: 'bg-orange-50 text-orange-700' };
    if (slot.dynamic)
      return { icon: <Wand2 className="h-3 w-3" />, label: 'Auto by category', cls: 'bg-slate-100 text-slate-600' };
    if (pinCount > 0)
      return { icon: <Pin className="h-3 w-3" />, label: `${pinCount} pinned`, cls: 'bg-blue-50 text-blue-700' };
    if (hasOverride)
      return { icon: <Sparkles className="h-3 w-3" />, label: 'Custom card', cls: 'bg-violet-50 text-violet-700' };
    return { icon: <Sparkles className="h-3 w-3" />, label: 'Automatic', cls: 'bg-slate-100 text-slate-500' };
  })();

  return (
    <button
      type="button"
      onClick={() => onOpen(slot)}
      className={cn(
        'group relative flex min-h-[92px] flex-col justify-between overflow-hidden rounded-xl border-2 border-gray-100 bg-white p-3 text-left shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        accent.ring,
        className,
      )}
    >
      <div className={cn('pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100', accent.glow)} />
      <div className="relative flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900">{slot.name}</span>
        <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', status.cls)}>
          {status.icon}
          {status.label}
        </span>
      </div>

      <div className="relative space-y-1">
        {slot.feed === 'highlights' && slot.etid != null && !slot.dynamic && (
          <span className={cn('inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium', accent.chip)}>
            {pillarName(slot.etid)}
          </span>
        )}
        <p className="text-[11px] leading-snug text-gray-500 line-clamp-2">{describeDefaultSource(slot)}</p>
      </div>

      <span className="relative mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">
        <MousePointerClick className="h-3 w-3" />
        {editable ? 'Manage this spot' : 'View details'}
      </span>
    </button>
  );
}
